import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, Home, Send, ShieldQuestion, Users, History, Building2,
  CreditCard, Settings, User, LogOut, ChevronDown, Check, Gauge, Menu, X,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { LanguageSelector } from "../LanguageSelector";
import { useAuth } from "../../context/AuthContext";

const navClass = (active, primary = false) => {
  if (primary) {
    return active
      ? "bg-emerald-600 text-white shadow-sm"
      : "bg-emerald-500 text-emerald-950 hover:bg-emerald-400";
  }
  return active
    ? "bg-slate-100 text-slate-950 font-semibold"
    : "text-slate-600 hover:text-slate-950 hover:bg-slate-50";
};

const WorkspaceSwitcher = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { workspaces, activeWorkspace, switchWorkspace } = useAuth();
  const label = activeWorkspace?.is_personal
    ? t("workspace.mySpace", { defaultValue: "Espace personnel" })
    : (activeWorkspace?.name || t("workspace.fallback", { defaultValue: "Workspace" }));

  if (!workspaces?.length) {
    return (
      <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-600">
        <Building2 className="w-4 h-4 text-emerald-600" /> Workspace
      </div>
    );
  }

  if (workspaces.length === 1) {
    return (
      <div data-testid="app-workspace-current" className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white min-w-0 max-w-[260px]">
        <Building2 className="w-4 h-4 text-emerald-600 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{label}</div>
          <div className="text-[11px] text-slate-400 truncate">
            {t(`roles.${activeWorkspace?.role}`, { defaultValue: activeWorkspace?.role_label || activeWorkspace?.role || "" })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button data-testid="app-workspace-switcher" variant="outline" className="border-slate-200 bg-white hover:bg-slate-50 text-slate-900 max-w-[280px] h-auto py-2">
          <Building2 className="w-4 h-4 mr-2 text-emerald-600 shrink-0" />
          <span className="truncate">{label}</span>
          <ChevronDown className="w-4 h-4 ml-2 text-slate-400 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel>Workspace actif</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {workspaces.map((w) => (
          <DropdownMenuItem
            key={w.id}
            className="cursor-pointer"
            onClick={() => {
              switchWorkspace(w.id);
              navigate("/compte");
            }}
          >
            <div className="flex items-center justify-between gap-3 w-full">
              <div className="min-w-0">
                <div className="font-medium truncate">{w.is_personal ? t("workspace.mySpace", { defaultValue: "Espace personnel" }) : w.name}</div>
                <div className="text-xs text-slate-400">{t(`roles.${w.role}`, { defaultValue: w.role_label || w.role })}</div>
              </div>
              {activeWorkspace?.id === w.id && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/compte/profil")}>Voir mes Workspaces</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ConnectedAppShell = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(["account", "common", "files"]);
  const { user, activeWorkspace, logout } = useAuth();

  const role = activeWorkspace?.role || "member";
  const canOperate = role !== "billing_admin";
  const canDirectory = role !== "billing_admin";
  const canBilling = role === "owner" || role === "admin" || role === "billing_admin";
  const canAdmin = role === "owner" || role === "admin";
  const isPlatformAdmin = user?.role === "admin";

  const items = [
    { key: "home", label: "Accueil", path: "/compte", icon: Home, exact: true, show: true },
    { key: "send", label: "Envoyer", path: "/envoyer", icon: Send, primary: true, show: canOperate, aliases: ["/generateur", "/fichiers"] },
    { key: "request", label: t("common:nav.request", { defaultValue: "Demander" }), path: "/demander", icon: ShieldQuestion, show: canOperate },
    { key: "directory", label: t("account:dashboard.annuaire", { defaultValue: "Annuaire" }), path: "/compte/annuaire", icon: Users, show: canDirectory },
    { key: "history", label: t("account:dashboard.historique", { defaultValue: "Historique" }), path: "/compte/historique", icon: History, show: true },
    { key: "workspace", label: t("account:dashboard.monWorkspace", { defaultValue: "Mon Workspace" }), path: "/compte/workspace", icon: Building2, show: role !== "billing_admin" },
    { key: "billing", label: t("account:dashboard.facturation", { defaultValue: "Facturation" }), path: "/compte/facturation", icon: CreditCard, show: canBilling },
    { key: "admin", label: t("account:dashboard.administration", { defaultValue: "Administration" }), path: "/compte/administration", icon: Settings, show: canAdmin },
    { key: "platform", label: "Console plateforme", path: "/admin", icon: Gauge, show: isPlatformAdmin },
  ].filter((i) => i.show);

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    if (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)) return true;
    return (item.aliases || []).some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
  };

  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.name || user?.email || "Compte";

  const go = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  const navContent = (
    <>
      <div className="px-3 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Navigation</div>
      <nav className="space-y-1 px-2" aria-label="Navigation de l'espace connecté">
        {items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <button
              key={item.key}
              data-testid={`app-nav-${item.key}`}
              onClick={() => go(item.path)}
              className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-left transition-colors ${navClass(active, item.primary)}`}
              aria-current={active ? "page" : undefined}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );

  const bottomItems = role === "billing_admin"
    ? items.filter((i) => ["home", "billing", "history"].includes(i.key))
    : items.filter((i) => ["home", "send", "history"].includes(i.key));

  return (
    <div className="app-bg grain min-h-screen relative">
      <header className="sticky top-0 z-50 h-16 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="h-full px-4 lg:px-6 flex items-center gap-3">
          <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100" onClick={() => setMobileOpen(true)} aria-label="Ouvrir le menu">
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/compte" className="flex items-center gap-2.5 shrink-0">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="font-heading font-extrabold tracking-tight text-lg hidden sm:block">Secure<span className="text-emerald-600">Pass</span></div>
          </Link>

          <div className="hidden sm:block h-7 w-px bg-slate-200 mx-1" />
          <div className="min-w-0 flex-1"><WorkspaceSwitcher /></div>

          <LanguageSelector />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="app-profile-menu" variant="ghost" className="max-w-[230px] px-2 sm:px-3">
                <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-slate-600" />
                </div>
                <span className="hidden md:block truncate ml-2 text-sm font-medium">{displayName}</span>
                <ChevronDown className="w-4 h-4 ml-1 text-slate-400 hidden sm:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/compte/profil")}>
                <User className="w-4 h-4 mr-2" /> {t("account:dashboard.monProfil", { defaultValue: "Mon profil" })}
              </DropdownMenuItem>
              {isPlatformAdmin && (
                <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/admin")}>
                  <Gauge className="w-4 h-4 mr-2" /> Console plateforme
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer text-red-600 focus:text-red-600"
                onClick={() => { logout(); navigate("/"); }}
              >
                <LogOut className="w-4 h-4 mr-2" /> {t("account:dashboard.deconnexion", { defaultValue: "Déconnexion" })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-4rem)]">
        <aside className="hidden lg:block w-64 shrink-0 border-r border-slate-200 bg-white/75 backdrop-blur-sm">
          <div className="sticky top-16 pb-6">{navContent}</div>
        </aside>
        <main className="min-w-0 flex-1 px-4 sm:px-6 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-8">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] bg-slate-950/30" onClick={() => setMobileOpen(false)}>
          <aside className="w-[min(84vw,320px)] h-full bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="h-16 px-4 border-b border-slate-200 flex items-center justify-between">
              <div className="font-heading font-bold">Secure<span className="text-emerald-600">Pass</span></div>
              <button className="p-2 rounded-lg hover:bg-slate-100" onClick={() => setMobileOpen(false)} aria-label="Fermer le menu"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto h-[calc(100%-4rem)]">{navContent}</div>
          </aside>
        </div>
      )}

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 border-t border-slate-200 bg-white/95 backdrop-blur px-2 py-1.5 flex items-center justify-around" aria-label="Navigation rapide">
        {bottomItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <button key={item.key} onClick={() => go(item.path)} className={`min-w-[82px] rounded-lg px-3 py-2 flex flex-col items-center gap-1 text-[11px] font-medium ${active ? "text-emerald-700 bg-emerald-50" : "text-slate-500"}`}>
              <Icon className="w-4 h-4" />
              {item.label}
            </button>
          );
        })}
      </nav>
    </div>
  );
};
